import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { workQueueService, type WorkItem } from "@/core/api/services";
import { workbenchKeys } from "@/core/api/queryKeys";
import { useWorkItemActions } from "@/modules/queue/api/useWorkItemActions";
import { Button } from "@hub/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@hub/ui/components/ui/dialog";
import { Textarea } from "@hub/ui/components/ui/textarea";

interface WorkItemActionsProps {
  item: WorkItem;
}

export function WorkItemActions({ item }: WorkItemActionsProps) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const { transition, transitioning, addNote, addingNote, release, releasing } =
    useWorkItemActions();

  const { data: lifecycle } = useQuery({
    queryKey: workbenchKeys.lifecycles.detail(item.type),
    queryFn: async () => {
      const response = await workQueueService.getLifecycle({ type: item.type });
      return response.lifecycle ?? null;
    },
    enabled: !!item.type,
    staleTime: 60 * 1000,
  });

  const allowedTransitions =
    lifecycle?.transitions[item.status]?.targets ?? [];

  const handleTransition = async (newStatus: string) => {
    try {
      await transition({ id: item.id, newStatus });
    } catch {
      // Swallowed: prevents unhandled rejection; user sees no state change
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await addNote({ itemId: item.id, note: noteText.trim() });
      setNoteText("");
      setNoteOpen(false);
    } catch {
      // Swallowed: prevents unhandled rejection; user sees no state change
    }
  };

  const handleRelease = async () => {
    try {
      await release(item.id);
    } catch {
      // Swallowed: prevents unhandled rejection; user sees no state change
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {allowedTransitions.map((status) => (
          <Button
            key={status}
            variant="outline"
            size="sm"
            disabled={transitioning}
            onClick={() => handleTransition(status)}
          >
            {status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
          </Button>
        ))}

        <Button variant="outline" size="sm" onClick={() => setNoteOpen(true)}>
          Add Note
        </Button>

        {item.claimedBy && (
          <Button
            variant="outline"
            size="sm"
            disabled={releasing}
            onClick={handleRelease}
          >
            Release
          </Button>
        )}
      </div>

      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
          </DialogHeader>
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Type your note..."
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddNote} disabled={addingNote || !noteText.trim()}>
              {addingNote ? "Saving..." : "Save Note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
